import { Html } from "react-konva-utils";
import { Image, Rect } from "react-konva";
import { File } from "shared/datamodel/schemas";
import { useAtomValue } from "jotai";
import { imageCacheAtom, isStageReadyAtom } from "state-atoms";
import style from "./file-element.module.css";

function getPlaceholderStyles(width: number, height: number) {
  const spinnerSize = Math.min(width, height) * 0.15;
  const spinnerContainerStyle = {
    width: `${spinnerSize}px`,
    height: `${spinnerSize}px`,
    padding: `${spinnerSize * 0.1}px`,
    borderRadius: `${spinnerSize * 0.15}px`,
  };
  const spinnerStyle = {
    width: `${spinnerSize * 0.6}px`,
    height: `${spinnerSize * 0.6}px`,
    borderWidth: `${spinnerSize * 0.1}px`,
    borderRadius: "50%",
  };

  const blurAmount = Math.min(width, height) * 0.01;
  const thumbnailStyle = {
    filter: `blur(${blurAmount}px)`,
  };

  return { spinnerContainerStyle, spinnerStyle, thumbnailStyle };
}

export default function FileElementPlaceholder({
  element,
  isThumbnailExportMode,
}: {
  element: File;
  isThumbnailExportMode: boolean;
}) {
  const isStageReady = useAtomValue(isStageReadyAtom);
  const imageCache = useAtomValue(imageCacheAtom);
  const { fileId, width = 300, height = 150, thumbnail } = element;
  const { spinnerContainerStyle, spinnerStyle, thumbnailStyle } = getPlaceholderStyles(width, height);

  const cachedImage = fileId ? imageCache[fileId] : null;
  if (cachedImage) {
    return (
      <>
        <Image image={cachedImage as HTMLImageElement} visible={!isThumbnailExportMode} />
        <Html
          divProps={{
            style: {
              position: "absolute",
              top: 0,
              left: 0,
              pointerEvents: "none",
            },
          }}
        >
          <div className={style.filePlaceHolder} style={{ width, height }}>
            <div className={style.loadingSpinnerContainer} style={spinnerContainerStyle}>
              <div className={style.loadingSpinner} style={spinnerStyle} />
            </div>
          </div>
        </Html>
      </>
    );
  }

  if (thumbnail) {
    return (
      <>
        <Rect width={width} height={height} fillEnabled={false} strokeEnabled={false} />
        {isStageReady && (
          <Html
            divProps={{
              style: {
                position: "absolute",
                top: 0,
                left: 0,
                opacity: 0.6,
                pointerEvents: "none",
              },
            }}
          >
            <div className={style.filePlaceHolder} style={{ width, height }}>
              <img src={thumbnail} alt="thumbnail" className={style.thumbnail} style={thumbnailStyle} />
              <div className={style.thumbnailPlaceHolderContainer}>
                <div className={style.loadingSpinnerContainer} style={spinnerContainerStyle}>
                  <div className={style.loadingSpinner} style={spinnerStyle} />
                </div>
              </div>
            </div>
          </Html>
        )}
      </>
    );
  }

  return (
    <>
      <Rect width={width} height={height} fillEnabled={false} strokeEnabled={false} />
      {isStageReady && (
        <Html
          divProps={{
            style: {
              position: "absolute",
              top: 0,
              left: 0,
              pointerEvents: "none",
            },
          }}
        >
          <div className={style.filePlaceHolder} style={{ width, height }}>
            <div className={style.noThumbnailPlaceholder}>
              <div className={style.loadingSpinnerContainer} style={spinnerContainerStyle}>
                <div className={style.loadingSpinner} style={spinnerStyle} />
              </div>
            </div>
          </div>
        </Html>
      )}
    </>
  );
}
